import React, { useState, useEffect } from "react";
import { Box, Flex, HStack, Icon, Text, Tooltip } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import cronstrue from "cronstrue";
import { ScheduledJobProto } from "build-ts/schedule";
import WorkflowSectionHeading from "modules/analyst/components/WorkflowSectionHeading";
import { getTooltip } from "utils/getMessages";
import { MarkdownRenderer } from "modules/common/MarkdownRenderer";
import ArtifactPanelHeader from "../ArtifactPanelHeader";

type TabId = "analysis" | "instructions";

interface WorkflowCardProps {
  /** SCHEDULED_JOB artifact (from reasoning message) */
  scheduleJob?: ScheduledJobProto | null;
  /** Whether this job is already saved */
  isSavedInAnalyst: boolean;
  /** Whether the artifact panel is currently expanded to full width */
  isExpanded?: boolean;
  /** Called when the expand/collapse toggle button is clicked */
  onExpandToggle?: () => void;
  /** Restore to the tab the user was on when they last left this artifact */
  initialActiveTab?: string;
  /** Called when the user changes the active tab so the parent can persist it */
  onViewStateChange?: (view: { activeTab: string }) => void;
}

const WorkflowCard: React.FC<WorkflowCardProps> = ({
  scheduleJob: initialScheduleJob,
  isSavedInAnalyst,
  isExpanded,
  onExpandToggle,
  initialActiveTab,
  onViewStateChange,
}) => {
  const validTabs: TabId[] = ["analysis", "instructions"];
  const isValidWorkflowTab = (tab?: string): tab is TabId => !!tab && validTabs.includes(tab as TabId);

  const [activeTab, setActiveTab] = useState<TabId>(
    isValidWorkflowTab(initialActiveTab) ? initialActiveTab : "analysis"
  );

  const [jobName, setJobName] = useState(initialScheduleJob?.header?.name ?? "");
  const [trigger, setTrigger] = useState(initialScheduleJob?.scheduled_analysis?.trigger ?? "");
  const [reportingInstructions, setReportingInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.reporting_instructions ?? ""
  );
  const [analysisInstructions, setAnalysisInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.analysis_instructions ?? ""
  );
  const [cronExpression, setCronExpression] = useState(initialScheduleJob?.cron_expr ?? "");
  const [isScheduleActive, setIsScheduleActive] = useState(initialScheduleJob?.is_active ?? false);
  const [showCronRaw, setShowCronRaw] = useState(false);

  useEffect(() => {
    if (initialScheduleJob) {
      setJobName(initialScheduleJob.header?.name ?? "");
      setTrigger(initialScheduleJob.scheduled_analysis?.trigger ?? "");
      setReportingInstructions(initialScheduleJob.scheduled_analysis?.reporting_instructions ?? "");
      setAnalysisInstructions(initialScheduleJob.scheduled_analysis?.analysis_instructions ?? "");
      setCronExpression(initialScheduleJob.cron_expr ?? "");
      setIsScheduleActive(initialScheduleJob.is_active ?? false);
    } else {
      setJobName("");
      setTrigger("");
      setReportingInstructions("");
      setAnalysisInstructions("");
      setCronExpression("");
      setIsScheduleActive(false);
    }
  }, [initialScheduleJob]);

  const cronHumanReadable = (() => {
    if (!cronExpression.trim()) return null;
    try {
      return cronstrue.toString(cronExpression.trim());
    } catch {
      return null;
    }
  })();

  const displayLabel = jobName?.trim() || initialScheduleJob?.header?.name?.trim() || "New Job";

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: "analysis", label: "Workflow", icon: AppIcons.workflow },
    { id: "instructions", label: "Guidance", icon: AppIcons.guidance },
  ];

  // SAVED/UNSAVED status badge — shown in the header actions area
  const savedDraftBadge = isSavedInAnalyst ? (
    <Text
      fontSize="2xs"
      color="green.800"
      bg="green.100"
      px={2}
      py={0.5}
      borderRadius="sm"
      fontWeight="700"
      flexShrink={0}
      mb={0}
      border="1px solid"
      borderColor="green.200"
    >
      SAVED
    </Text>
  ) : (
    <Tooltip label="Please ask the analyst to save this workflow" hasArrow placement="top">
      <Text
        fontSize="2xs"
        color="orange.700"
        bg="yellow.100"
        px={2}
        py={0.5}
        borderRadius="sm"
        fontWeight="700"
        flexShrink={0}
        mb={0}
        border="1px solid"
        borderColor="yellow.500"
      >
        UNSAVED CHANGES
      </Text>
    </Tooltip>
  );

  return (
    <Box width="100%" h="100%" minH={0} bg="white" display="flex" flexDirection="column" overflow="hidden">
      <ArtifactPanelHeader
        icon={AppIcons.workflow}
        iconBg="white"
        iconColor="purple.700"
        name={displayLabel}
        typeLabel="Workflow"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={id => {
          const tab = id as TabId;
          setActiveTab(tab);
          onViewStateChange?.({ activeTab: tab });
        }}
        actions={savedDraftBadge}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
      />

      <Box
        flex={1}
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY={activeTab === "analysis" ? "hidden" : "auto"}
        sx={
          activeTab === "analysis"
            ? undefined
            : {
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: "var(--chakra-colors-gray-300)",
                  borderRadius: "2px",
                },
              }
        }
      >
        {activeTab === "analysis" && (
          <Flex direction="column" flex="1" minH={0} minW={0} w="100%" px={2} py={2} gap={2.5}>
            {/* Name */}
            <Box display="flex" flexDirection="column" minW={0} flexShrink={0}>
              <WorkflowSectionHeading label="Name" compact />
              <Text
                fontSize="0.8rem"
                fontWeight="500"
                color={jobName.trim() ? "gray.700" : "gray.400"}
                pl={2.5}
                mb={0}
                textAlign="left"
                display="block"
              >
                {jobName.trim() || "No name set."}
              </Text>
            </Box>

            {/* Objective */}
            <Box display="flex" flexDirection="column" minW={0} flexShrink={0}>
              <WorkflowSectionHeading
                label="Objective"
                compact
                tooltip={getTooltip("workflow_detail.objective.tooltip")}
              />
              <Box
                ml={2.5}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={3}
                py={0}
                bg="white"
                maxH="10rem"
                overflowY="auto"
                sx={{
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-track": { background: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    background: "var(--chakra-colors-gray-300)",
                    borderRadius: "2px",
                  },
                }}
              >
                {trigger.trim() ? (
                  <MarkdownRenderer content={trigger} isArtifactsPanelVisible={true} />
                ) : (
                  <Text fontSize="sm" fontWeight="400" color="gray.400" mb={0}>
                    No objective set.
                  </Text>
                )}
              </Box>
            </Box>

            {/* Run frequency + Status */}
            <Flex gap={2} align="flex-start" w="100%" flexShrink={0}>
              <Box flex="3" minW={0}>
                <WorkflowSectionHeading
                  label="Run frequency"
                  compact
                  tooltip={getTooltip("workflow_detail.schedule.tooltip")}
                  labelTrailing={
                    cronExpression.trim() ? (
                      <Tooltip
                        label={showCronRaw ? "Show human readable" : "Show cron expression"}
                        placement="top"
                        hasArrow
                        fontSize="xs"
                      >
                        <Box
                          as="button"
                          type="button"
                          onClick={() => setShowCronRaw(v => !v)}
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          w="1.1rem"
                          h="1.1rem"
                          borderRadius="sm"
                          bg={showCronRaw ? "purple.500" : "transparent"}
                          color={showCronRaw ? "white" : "gray.800"}
                          _hover={{ bg: showCronRaw ? "purple.600" : "gray.200" }}
                          flexShrink={0}
                        >
                          <Icon as={AppIcons.workflowRun} boxSize={3.5} />
                        </Box>
                      </Tooltip>
                    ) : undefined
                  }
                />
                <Text
                  fontSize="0.8rem"
                  fontWeight="500"
                  color={cronExpression.trim() ? "gray.700" : "gray.400"}
                  fontFamily={showCronRaw && cronExpression.trim() ? "mono" : "body"}
                  pl={2.5}
                  mb={0}
                  textAlign="left"
                  display="block"
                >
                  {cronExpression.trim()
                    ? showCronRaw
                      ? cronExpression.trim()
                      : (cronHumanReadable ?? cronExpression.trim())
                    : "No schedule set."}
                </Text>
              </Box>
              <Box flex="1" minW={0}>
                <WorkflowSectionHeading label="Status" compact />
                <HStack
                  spacing={1.5}
                  align="center"
                  bg={isScheduleActive ? "green.50" : "gray.100"}
                  px={2.5}
                  py={1}
                  ml={2.5}
                  mt={2.5}
                  w="fit-content"
                >
                  {isScheduleActive ? (
                    <Box w="6px" h="6px" borderRadius="full" bg="green.400" flexShrink={0} />
                  ) : (
                    <Icon as={AppIcons.pause} boxSize={2} color="gray.400" />
                  )}
                  <Text
                    as="span"
                    fontSize="2xs"
                    fontWeight="700"
                    letterSpacing="0.06em"
                    textTransform="uppercase"
                    color={isScheduleActive ? "green.700" : "gray.500"}
                    lineHeight={1}
                  >
                    {isScheduleActive ? "Live" : "Paused"}
                  </Text>
                </HStack>
              </Box>
            </Flex>

            {/* Instructions */}
            <Box display="flex" flexDirection="column" flex="1" minH={0} minW={0} w="100%">
              <WorkflowSectionHeading
                label="Instructions"
                compact
                tooltip={getTooltip("workflow_detail.instructions.tooltip")}
              />
              <Box position="relative" flex="1" minH={0} w="100%">
                <Box
                  position="absolute"
                  top={0}
                  bottom={0}
                  left={2.5}
                  right={0}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={3}
                  py={0}
                  bg="white"
                  overflowY="auto"
                  overflowX="auto"
                  sx={{
                    "&::-webkit-scrollbar": { width: "4px" },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      background: "var(--chakra-colors-gray-300)",
                      borderRadius: "2px",
                    },
                  }}
                >
                  {reportingInstructions.trim() ? (
                    <MarkdownRenderer content={reportingInstructions} isArtifactsPanelVisible={true} />
                  ) : (
                    <Text fontSize="sm" fontWeight="400" color="gray.400" mb={0}>
                      No instructions set.
                    </Text>
                  )}
                </Box>
              </Box>
            </Box>
          </Flex>
        )}

        {activeTab === "instructions" && (
          <Flex direction="column" flex="1" minH={0} w="100%" overflow="hidden" px={2} py={2}>
            <Box display="flex" flexDirection="column" flex="1" minH={0} w="100%">
              <WorkflowSectionHeading
                label="For Internal Use Only"
                compact
                tooltip="Internal guidance for the agent to perform workflow tasks more reliably."
              />
              <Box
                flex="1"
                minH={0}
                w="100%"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={4}
                py={3}
                bg="gray.50"
                overflowY="auto"
                overflowX="auto"
              >
                {analysisInstructions.trim() ? (
                  <MarkdownRenderer content={analysisInstructions} isArtifactsPanelVisible={true} />
                ) : (
                  <Text fontSize="sm" fontWeight="400" color="gray.400" mb={0}>
                    No guidance set.
                  </Text>
                )}
              </Box>
            </Box>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default WorkflowCard;
